<template>
  <transition name="bounce">
    <b-container class="py-5 custom-container container">
      <b-breadcrumb
        :items="items"
        class="pl-0 pt-5 mt-5 bg-transparent"
      ></b-breadcrumb>
      <h2 class="font-weight-bold">Edit file</h2>
      <b-card v-if="file" class="mt-4">
        <div class="mx-auto p-lg-5 w-75">
          <b-alert v-if="isRenewalRequest" show variant="warning" class="mb-4">
            This file was flagged as expiring soon - update the
            <strong>Expiry Date</strong> below (highlighted) and re-upload the
            renewed document to complete the request.
          </b-alert>
          <h4 class="font-weight-bold mb-4">File Details</h4>
          <file-form
            :file="file"
            :new="false"
            :is-request-file-creation="returnToRequestFilePage"
            :request="requestContext"
            :back-to="backTo"
            :highlight-expiry="isRenewalRequest"
          />
        </div>
      </b-card>
    </b-container>
  </transition>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import FileForm from '@/components/forms/FileForm'
import { canViewerEditFile } from '@/utils/filePermissions'

export default {
  data: function () {
    return {
      originName: null,
      originFullPath: null,
      originParams: null,
      loading: false,
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.originName = from.name || null
      vm.originFullPath = from.fullPath || null
      vm.originParams = from.params ? { ...from.params } : null
    })
  },
  components: {
    FileForm,
  },

  computed: {
    ...mapState({
      file: (state) => state.file.file,
      user: (state) => state.auth.user,
    }),
    ...mapGetters('auth', ['hasPermission']),

    // Set on the redirect from clicking a "renewal requested" notification
    // (NotificationsList.goToFileRequest) — see the query param it appends.
    isRenewalRequest() {
      return this.$route.query.renewal === '1'
    },

    returnToRequestFilePage() {
      return (
        this.$route.params.isRequestFilePage === true ||
        this.$route.params.isRequestsFilePage === 'true' ||
        this.$route.query.fromRequest === 'true'
      )
    },

    backTo() {
      // Prefer the exact route the user came from so reloads/deep-links degrade
      // gracefully to a sensible fallback.
      const KNOWN_BACK_ROUTES = [
        'file.list-page',
        'file.view-page',
        'tradies.view-tradie',
        'projects.view-tradie',
        'projects.view-page',
        'requests.list-page',
        'templates.list-page',
        'home.welcome-page',
      ]
      if (this.originName && KNOWN_BACK_ROUTES.includes(this.originName)) {
        return this.originFullPath || { name: this.originName }
      }
      if (this.returnToRequestFilePage) {
        return { name: 'requests.list-page' }
      }
      return { name: 'file.list-page' }
    },

    requestContext() {
      const id = this.$route.query.requestFileId || this.$route.params.id
      return this.returnToRequestFilePage && id
        ? {
            id,
            name: this.file ? this.file.name : '',
            information_types:
              (this.file && this.file.file_information_types) || [],
          }
        : null
    },
    items() {
      return [
        {
          text: this.file ? this.file.name : '',
          to: '/file/' + this.$route.params.id,
        },
        { text: 'Edit' },
      ]
    },
  },

  methods: {
    ...mapActions({
      getFile: 'file/getFile',
    }),
    ...mapMutations({
      clearFile: 'file/clearFile',
    }),

    async fetchFile() {
      if (this.loading) return
      this.loading = true
      // Vuex `file` state isn't reset between page visits, so `v-if="file"`
      // below can otherwise go true instantly with a *previous* file still
      // in state — mounting FileForm (and its one-time resetForm() snapshot)
      // against stale data a moment before this fetch replaces it. Clearing
      // first keeps the form gated behind the real fetch, avoiding fields
      // that render one file but back the Next-button validation with
      // another (Date of Issue/Expiry Date silently stuck "missing").
      this.clearFile()
      try {
        await this.getFile(this.$route.params.id)
      } catch (e) {
        this.$bvToast.toast('Could not load this file. Please try again.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.loading = false
      }
    },

    redirectIfCannotEdit() {
      const isBusinessAdmin =
        this.user && this.user.user_type === 'business_admin'
      if (!isBusinessAdmin) return
      if (canViewerEditFile(this.file, this.user, this.hasPermission)) return

      this.$router.replace({
        name: 'file.view-page',
        params: { id: this.$route.params.id },
      })
    },
  },

  async created() {
    await this.fetchFile()
    this.redirectIfCannotEdit()
  },
}
</script>
